import React from 'react';
import { Box, Button, Divider, Link, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import { logError } from '../../Helpers/errorLogger';
import {
  SUPPORT_EMAIL,
  getRecentConsoleLogs,
  formatErrorForEmail,
  filterSensitiveData,
} from '../../Helpers/supportContact';

/**
 * Top-level error boundary for the entire application.
 *
 * Catches any uncaught render-time errors that bubble up past more specific
 * boundaries (e.g. AgGridErrorBoundary) and renders a full-page fallback
 * instead of a blank white screen.
 *
 * Includes:
 * - A "Reload Page" button for quick recovery
 * - A "Copy error details" button that puts the full error + console log onto the clipboard
 * - A "Contact Support" email link with the error report pre-populated in the body
 *
 * Note: this is a class component because error boundaries require lifecycle methods
 * (getDerivedStateFromError / componentDidCatch) that are not available in hooks.
 * It cannot safely use React context (the context provider may be part of the crash
 * tree), so user info is omitted from the report body.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      // Report and mailto are built once in componentDidCatch and cached here
      // so render() never recomputes them (avoids re-running on copy-button re-renders).
      cachedReport: null,
      cachedMailto: null,
      copied: false,
      clipboardUnavailable: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logError(error, {
      source: 'AppErrorBoundary',
      componentStack: errorInfo?.componentStack,
    });

    // Build the report once here, while all error context is fresh, and cache it.
    const report = this._buildReport(error, errorInfo);
    const mailtoLink = this._buildMailto(report);

    this.setState({ errorInfo, cachedReport: report, cachedMailto: mailtoLink });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Builds the plain-text error report. Called once in componentDidCatch.
   * Static so it can run before this.state.errorInfo is set.
   */
  _buildReport(error, errorInfo) {
    const errorMessage = error ? error.toString() : 'Unknown error';
    const stack = error?.stack || '';
    const componentStack = errorInfo?.componentStack || '';
    const recentLogs = getRecentConsoleLogs(10);

    return formatErrorForEmail({
      errorMessage: filterSensitiveData(errorMessage),
      errorDetails: {
        stack: filterSensitiveData(
          [stack, componentStack ? `\nComponent Stack:${componentStack}` : '']
            .filter(Boolean)
            .join('')
        ).substring(0, 2000),
      },
      context: {
        url: window.location.href,
      },
      recentLogs,
    });
  }

  _buildMailto(report) {
    try {
      // mailto bodies have a ~2000-char practical limit across email clients
      const body = report.length > 1800
        ? report.substring(0, 1800) + '\n\n[Truncated — attach clipboard copy for full details]'
        : report;
      return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('App Crash Report')}&body=${encodeURIComponent(body)}`;
    } catch {
      return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('App Crash Report')}`;
    }
  }

  handleCopy = () => {
    const report = this.state.cachedReport || '';

    if (!navigator.clipboard) {
      // Clipboard API unavailable — show feedback instead of silently failing
      this.setState({ clipboardUnavailable: true });
      return;
    }

    navigator.clipboard.writeText(report).then(() => {
      this.setState({ copied: true, clipboardUnavailable: false });
      setTimeout(() => this.setState({ copied: false }), 3000);
    }).catch(() => {
      this.setState({ clipboardUnavailable: true });
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, copied, clipboardUnavailable, cachedMailto } = this.state;

    const copyLabel = () => {
      if (copied) return 'Copied!';
      if (clipboardUnavailable) return 'Clipboard unavailable';
      return 'Copy error details';
    };

    return (
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          justifyContent: 'center',
          minHeight: '100vh',
          p: 4,
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 64 }} />

        {/* Heading */}
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
            An unexpected error occurred and the app could not continue. Your data
            has not been lost — reloading the page should get you back to where you were.
          </Typography>
        </Box>

        {/* Primary action */}
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<RefreshIcon />}
          onClick={this.handleReload}
        >
          Reload Page
        </Button>

        <Divider sx={{ width: '100%', maxWidth: 480 }} />

        {/* Support section */}
        <Box sx={{ maxWidth: 520 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            If this keeps happening, please let us know. Copy the error details
            below and include them when you reach out — it helps us fix the issue faster.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Copy to clipboard */}
            <Button
              variant="outlined"
              size="small"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={this.handleCopy}
              color={copied ? 'success' : clipboardUnavailable ? 'warning' : 'inherit'}
            >
              {copyLabel()}
            </Button>

            {/* Email support — mailto pre-populated with error report */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<EmailIcon />}
              component={Link}
              href={cachedMailto || `mailto:${SUPPORT_EMAIL}`}
              color="primary"
              sx={{ textDecoration: 'none' }}
            >
              Email support
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Or email us directly at{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ fontWeight: 500 }}>
              {SUPPORT_EMAIL}
            </Link>
          </Typography>
        </Box>

        {/* Technical detail panel — dev/staging only */}
        {process.env.NODE_ENV !== 'production' && error && (
          <Box
            sx={{
              bgcolor: 'grey.100',
              borderRadius: 2,
              maxWidth: 700,
              mt: 1,
              p: 2,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <Typography
              variant="caption"
              color="error"
              sx={{
                display: 'block',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {error.toString()}
            </Typography>
            {this.state.errorInfo?.componentStack && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  fontFamily: 'monospace',
                  mt: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {this.state.errorInfo.componentStack}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
}

export default AppErrorBoundary;
